import { useMemo, useState } from 'react';
import { Boxes, Info, Sparkles } from 'lucide-react';
import {
  DataTable,
  StatusPill,
  EmptyState,
  type ColumnDef,
  type StatusTone,
} from '@vnetwork/ui/data';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useProjectResources, type ProjectResource } from '@/api/projects';
import { AutoDiscoveryWizard } from '@/components/resources/AutoDiscoveryWizard';
import { formatDate } from './shared';

function resourceTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case 'active':
    case 'running':
      return 'success';
    case 'pending':
    case 'provisioning':
      return 'warning';
    case 'error':
    case 'failed':
      return 'danger';
    default:
      return 'muted';
  }
}

interface ProjectResourcesTabProps {
  projectId: string;
  /** Whether the current user may assign resources (owner/admin). */
  canManage?: boolean;
}

export function ProjectResourcesTab({ projectId, canManage = false }: ProjectResourcesTabProps) {
  const { data: resources = [], isLoading } = useProjectResources(projectId);
  const [discoverOpen, setDiscoverOpen] = useState(false);

  // Breakdowns are derived from the (full) resource list — the backend stats
  // endpoint only returns counts by service, not by status.
  const stats = useMemo(() => {
    const byService: Record<string, number> = {};
    const byStatus: Record<string, number> = {};
    for (const r of resources) {
      byService[r.service_id] = (byService[r.service_id] ?? 0) + 1;
      byStatus[r.resource_status] = (byStatus[r.resource_status] ?? 0) + 1;
    }
    return { total: resources.length, byService, byStatus };
  }, [resources]);

  const columns = useMemo<ColumnDef<ProjectResource, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Resource',
        accessorKey: 'resource_name',
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontWeight: 500 }}>
            <Boxes size={14} color="var(--vn-fg-muted)" />
            {row.original.resource_name}
          </span>
        ),
      },
      {
        id: 'service',
        header: 'Service',
        accessorKey: 'service_id',
        cell: ({ getValue }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{getValue() as string}</span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'resource_type',
        cell: ({ getValue }) => (
          <Badge tone="neutral" appearance="outline" size="sm">
            {getValue() as string}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={resourceTone(row.original.resource_status)}>
            {row.original.resource_status}
          </StatusPill>
        ),
      },
      {
        id: 'assigned',
        header: 'Assigned',
        accessorKey: 'assigned_at',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{formatDate(row.original.assigned_at)}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div
        style={{
          display: 'grid',
          gap: 'var(--vn-space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}
      >
        <Card flat>
          <CardBody>
            <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Total resources</div>
            <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {stats.total}
            </div>
          </CardBody>
        </Card>
        <Card flat>
          <CardBody>
            <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', marginBottom: 'var(--vn-space-2)' }}>
              By service
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {Object.keys(stats.byService).length > 0 ? (
                Object.entries(stats.byService).map(([svc, count]) => (
                  <Badge key={svc} tone="neutral" appearance="subtle" size="sm">
                    {svc}: {count}
                  </Badge>
                ))
              ) : (
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 13 }}>No resources yet</span>
              )}
            </div>
          </CardBody>
        </Card>
        <Card flat>
          <CardBody>
            <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', marginBottom: 'var(--vn-space-2)' }}>
              By status
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {Object.keys(stats.byStatus).length > 0 ? (
                Object.entries(stats.byStatus).map(([status, count]) => (
                  <StatusPill key={status} tone={resourceTone(status)}>
                    {status}: {count}
                  </StatusPill>
                ))
              ) : (
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 13 }}>No resources yet</span>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {canManage ? (
        <Alert
          tone="info"
          icon={<Info size={16} />}
          actions={
            <Button size="sm" startIcon={<Sparkles size={15} />} onClick={() => setDiscoverOpen(true)}>
              Auto Discover
            </Button>
          }
        >
          Gather existing domains and instances into this project by conditions, or assign them
          individually from each service module.
        </Alert>
      ) : (
        <Alert tone="info" icon={<Info size={16} />}>
          Resources are assigned to a project from within each service module. This view is
          read-only.
        </Alert>
      )}

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : resources.length === 0 ? (
        <EmptyState
          icon={<Boxes size={28} />}
          title="No resources assigned"
          description="Assign resources to this project from a service module to see them here."
        />
      ) : (
        <DataTable columns={columns} data={resources} getRowId={(r) => r.id} />
      )}

      {canManage ? (
        <Dialog open={discoverOpen} onOpenChange={setDiscoverOpen}>
          <DialogContent size="lg">
            <DialogHeader>
              <DialogTitle>Auto Discover resources</DialogTitle>
              <DialogDescription>
                Find domains and instances by conditions and assign them to this project.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <AutoDiscoveryWizard
                targetProjectId={projectId}
                onClose={() => setDiscoverOpen(false)}
                onDone={() => setDiscoverOpen(false)}
              />
            </DialogBody>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
